import { useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import LeftInformationPanel from "./components/LeftInformationPanel";
import TopNavBar from "./components/TopNavBar";

import axios from "axios";

// Public pages
import Login from "./pages/Login/Login";
import Register from "./pages/Login/Register";
import ForgotPassword from "./pages/Login/ForgotPassword";
import ResetPassword from "./pages/Login/ResetPassword";

// Protected pages
import OpenPositions from "./pages/Home/OpenPositions";
import ProfilePage from "./pages/Profile/ProfilePage";
import CandidateOnboardingPage from "./pages/Home/CandidateOnboardingPage";
import ApplyJobPage from "./pages/Home/ApplyJobPage";

// Layout
import AppLayout from "./components/AppLayout";

import "./styles/App.css";
import "./styles/OpenPositions.css";
import authStore from "./utils/authStorage";

function RouteGuard({ children }) {
  const [loading, setLoading] = useState(true);
  const [forceOnboarding, setForceOnboarding] = useState(false);
  const navigate = useNavigate();
  const BASE = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return setLoading(false);

        const { data } = await axios.get(`${BASE}/api/apply/check`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (data.pipeline_status === 2) {
          setForceOnboarding(true);
          navigate(`/candidate/onboarding/${data.application_id}`, {
            replace: true,
          });
        }
      } catch {
        setForceOnboarding(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, BASE]);

  if (loading) return null;
  if (forceOnboarding) return null;
  return children;
}

export default function App() {
  useEffect(() => {
    authStore.processPendingLogoutOnLoad();
    authStore.ensureTabSession();
    authStore.installUnloadHook();
  }, []);

  const authed = useMemo(() => !!localStorage.getItem("token"), []);

  return (
    <div className="app-container">
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />

        {/* Open Positions Page (custom layout with sidebar) */}
        <Route
          path="/"
          element={
            <>
              <TopNavBar isLoggedIn={authed} />
              <div className="open-positions-layout">
                <LeftInformationPanel />
                <div className="open-positions-main">
                  <OpenPositions />
                </div>
              </div>
            </>
          }
        />

        {/* Protected pages */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <RouteGuard>
                <AppLayout>
                  <ProfilePage />
                </AppLayout>
              </RouteGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/apply/:jobId"
          element={
            <ProtectedRoute>
              <RouteGuard>
                <AppLayout>
                  <ApplyJobPage />
                </AppLayout>
              </RouteGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/candidate/onboarding/:applicationId"
          element={
            <ProtectedRoute>
              <AppLayout>
                <CandidateOnboardingPage
                  userId={localStorage.getItem("userId")}
                />
              </AppLayout>
            </ProtectedRoute>
          }
        />

        <Route
          path="*"
          element={<Navigate to={authed ? "/" : "/login"} replace />}
        />
      </Routes>
    </div>
  );
}
